import React, { memo, useMemo } from 'react'

import type { StyledSvgComponent } from '../../../types/svg'
import {
    ButtonWrapper,
    IconImage,
    DrawerItem,
    DrawerItemLeft,
    DrawerItemText,
    GreetingWrapper,
    ProfileDrawerHeader,
    GreetingTitle,
    GreetingSubtitle,
} from './header-section-styles'
import Drawer, { Direction } from '../drawer'
import { HeaderSectionTransport } from '../../../transports/region-landing-page-transport'
import { useTranslation } from '../../../hooks/locale'

import CloseRegularIcon from '../../../assets/icons/close-regular.svg'
import RightIcon from '../../../assets/icons/chevron-right.svg'
import LandlordAreaIcon from '../../../assets/icons/userbar-icons/usermenu-icons/layout.svg'
import BookingIcon from '../../../assets/icons/userbar-icons/bookings.svg'
import LandlordListingsIcon from '../../../assets/icons/userbar-icons/usermenu-icons/megaphone.svg'
import LandlordInvoicesIcon from '../../../assets/icons/userbar-icons/usermenu-icons/file-text.svg'
import PersonalDetailsIcon from '../../../assets/icons/userbar-icons/personal-details.svg'
import LogoutIcon from '../../../assets/icons/userbar-icons/log-out.svg'

interface Props {
    open: boolean
    onClose: () => void
    headerSection: HeaderSectionTransport
    isLandlordRole?: boolean
    isGuestRole?: boolean
    firstName?: string
    inquiryCount?: number
}

const ProfileDrawer: React.FC<Props> = ({
    open,
    onClose,
    headerSection,
    isLandlordRole,
    isGuestRole,
    firstName,
    inquiryCount,
}) => {
    const { t } = useTranslation()

    const {
        logoutButton,
        guestProfileButton,
        landlordProfileButton,
        userBookingsButton,
        guestInquiriesButton,
        landlordInquiriesButton,
        landlordListingsButton,
        landlordInvoicesButton,
        landlordAreaButton,
    } = headerSection.navigationSectionResponse.navigation

    const drawerHeight = useMemo(() => (isLandlordRole ? 470 : 315), [isLandlordRole])

    return (
        <Drawer open={open} onClose={onClose} direction={Direction.bottom} size={drawerHeight} id="usersidebar">
            <ProfileDrawerHeader>
                <ButtonWrapper onClick={onClose}>
                    <IconImage as={CloseRegularIcon as StyledSvgComponent} />
                </ButtonWrapper>
                <GreetingWrapper>
                    <GreetingTitle>
                        {t('userGreet')} {firstName}
                    </GreetingTitle>
                    <GreetingSubtitle>
                        {isLandlordRole ? t('landlordUserLoginMsg') : t('guestUserLoginMsg')}
                    </GreetingSubtitle>
                </GreetingWrapper>
            </ProfileDrawerHeader>
            {isLandlordRole && !!landlordAreaButton?.url && (
                <DrawerItem href={landlordAreaButton.url} onClick={onClose} noCrawl={landlordAreaButton.noCrawl}>
                    <DrawerItemLeft>
                        <IconImage as={LandlordAreaIcon as StyledSvgComponent} />
                        <DrawerItemText>{landlordAreaButton.text}</DrawerItemText>
                    </DrawerItemLeft>
                    <IconImage as={RightIcon as StyledSvgComponent} />
                </DrawerItem>
            )}
            {isGuestRole && !!guestInquiriesButton?.url && (
                <DrawerItem href={guestInquiriesButton.url} onClick={onClose} noCrawl={guestInquiriesButton.noCrawl}>
                    <DrawerItemLeft>
                        <IconImage as={BookingIcon as StyledSvgComponent} />
                        <DrawerItemText>
                            {guestInquiriesButton.text}
                            {inquiryCount ? ` [${inquiryCount}]` : ''}
                        </DrawerItemText>
                    </DrawerItemLeft>
                    <IconImage as={RightIcon as StyledSvgComponent} />
                </DrawerItem>
            )}
            {isLandlordRole && !!landlordInquiriesButton?.url && (
                <DrawerItem
                    href={landlordInquiriesButton.url}
                    onClick={onClose}
                    noCrawl={landlordInquiriesButton.noCrawl}
                >
                    <DrawerItemLeft>
                        <IconImage as={BookingIcon as StyledSvgComponent} />
                        <DrawerItemText>
                            {landlordInquiriesButton.text}
                            {inquiryCount ? ` [${inquiryCount}]` : ''}
                        </DrawerItemText>
                    </DrawerItemLeft>
                    <IconImage as={RightIcon as StyledSvgComponent} />
                </DrawerItem>
            )}
            {!!userBookingsButton?.url && (
                <DrawerItem href={userBookingsButton.url} onClick={onClose} noCrawl={userBookingsButton.noCrawl}>
                    <DrawerItemLeft>
                        <IconImage as={BookingIcon as StyledSvgComponent} />
                        <DrawerItemText>{userBookingsButton.text}</DrawerItemText>
                    </DrawerItemLeft>
                    <IconImage as={RightIcon as StyledSvgComponent} />
                </DrawerItem>
            )}
            {isLandlordRole && !!landlordListingsButton?.url && (
                <DrawerItem
                    href={landlordListingsButton.url}
                    onClick={onClose}
                    noCrawl={landlordListingsButton.noCrawl}
                >
                    <DrawerItemLeft>
                        <IconImage as={LandlordListingsIcon as StyledSvgComponent} />
                        <DrawerItemText>{landlordListingsButton.text}</DrawerItemText>
                    </DrawerItemLeft>
                    <IconImage as={RightIcon as StyledSvgComponent} />
                </DrawerItem>
            )}
            {isLandlordRole && !!landlordInvoicesButton?.url && (
                <DrawerItem
                    href={landlordInvoicesButton.url}
                    onClick={onClose}
                    noCrawl={landlordInvoicesButton.noCrawl}
                >
                    <DrawerItemLeft>
                        <IconImage as={LandlordInvoicesIcon as StyledSvgComponent} />
                        <DrawerItemText>{landlordInvoicesButton.text}</DrawerItemText>
                    </DrawerItemLeft>
                    <IconImage as={RightIcon as StyledSvgComponent} />
                </DrawerItem>
            )}
            {isGuestRole && !!guestProfileButton?.url && (
                <DrawerItem href={guestProfileButton.url} onClick={onClose} noCrawl={guestProfileButton.noCrawl}>
                    <DrawerItemLeft>
                        <IconImage as={PersonalDetailsIcon as StyledSvgComponent} />
                        <DrawerItemText>{guestProfileButton.text}</DrawerItemText>
                    </DrawerItemLeft>
                    <IconImage as={RightIcon as StyledSvgComponent} />
                </DrawerItem>
            )}
            {isLandlordRole && !!landlordProfileButton?.url && (
                <DrawerItem href={landlordProfileButton.url} onClick={onClose} noCrawl={landlordProfileButton.noCrawl}>
                    <DrawerItemLeft>
                        <IconImage as={PersonalDetailsIcon as StyledSvgComponent} />
                        <DrawerItemText>{landlordProfileButton.text}</DrawerItemText>
                    </DrawerItemLeft>
                    <IconImage as={RightIcon as StyledSvgComponent} />
                </DrawerItem>
            )}
            {!!logoutButton?.url && (
                <DrawerItem href={logoutButton.url} onClick={onClose} noCrawl={logoutButton.noCrawl}>
                    <DrawerItemLeft>
                        <IconImage as={LogoutIcon as StyledSvgComponent} />
                        <DrawerItemText>{logoutButton.text}</DrawerItemText>
                    </DrawerItemLeft>
                    <IconImage as={RightIcon as StyledSvgComponent} />
                </DrawerItem>
            )}
        </Drawer>
    )
}

export default memo(ProfileDrawer)
